/**
 * cyfunFramework.ts - Référentiel CyFun officiel (CCB CyberFundamentals
 * v2024-01-08), extrait de "CyFun mapping Public_v20240108(2).xlsx".
 *
 * 219 exigences ordonnées (Function -> Category -> Subcategory -> Requirement),
 * chacune avec son niveau (Basic/Important/Essential), son flag Key Measure,
 * ses références (ISO 27001/27002, CIS v8), et le MAPPING vers les contrôles
 * NIS365 qui la couvrent (auto M365, auto Azure, manuel) -> permet de déduire
 * le type (Auto M365 / Auto Azure / Manuel / Externe) + de relier les
 * résultats de scan (conformité, preuve, date) à chaque exigence.
 *
 * Données dans cyfunFramework.json (généré depuis l'Excel - NE PAS éditer à la
 * main : régénérer via le script d'extraction si le référentiel évolue).
 */
import dataRaw from './cyfunFramework.json';

export type CyFunLevel = 'Basic' | 'Important' | 'Essential';

/** Type de couverture d'une exigence CyFun par NIS365. */
export type CyFunControlType = 'm365' | 'azure' | 'manual' | 'external';

export interface CyFunRequirement {
  /** ex: ID.AM-1.1 */
  id:           string;
  level:        CyFunLevel;
  /** Code fonction : ID, PR, DE, RS, RC */
  function:     string;
  /** ex: ID.AM */
  category:     string;
  /** ex: ID.AM-1 */
  subcategory:  string;
  requirement:  string;
  keyMeasure:   boolean;
  refs:         { iso27001: string | null; iso27002: string | null; cis: string | null };
  /** Type principal de couverture (priorité M365 > Azure > Manuel > Externe). */
  type:         CyFunControlType;
  /** Ids de contrôles NIS365 couvrant cette exigence (pour relier les scans). */
  m365:         string[];
  azure:        string[];
  manual:       string[];
}

export const CYFUN_REQUIREMENTS = dataRaw as unknown as CyFunRequirement[];

export const CYFUN_LEVELS: CyFunLevel[] = ['Basic', 'Important', 'Essential'];

/**
 * Un niveau cible inclut les niveaux inférieurs (cumulatif CyFun) :
 *  - Basic     -> Basic
 *  - Important -> Basic + Important
 *  - Essential -> Basic + Important + Essential
 */
export function levelsForTarget(target: CyFunLevel): CyFunLevel[] {
  if (target === 'Basic')     return ['Basic'];
  if (target === 'Important') return ['Basic', 'Important'];
  return ['Basic', 'Important', 'Essential'];
}

export const CYFUN_FUNCTION_LABELS: Record<string, string> = {
  ID: 'IDENTIFY',
  PR: 'PROTECT',
  DE: 'DETECT',
  RS: 'RESPOND',
  RC: 'RECOVER',
};

export const CYFUN_TYPE_LABELS: Record<CyFunControlType, string> = {
  m365:     'Auto M365',
  azure:    'Auto Azure',
  manual:   'Manuel',
  external: 'Contrôle externe',
};
